import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '../../../components/UI/Button';
import { Modal } from '../../../components/UI/Modal';
import { PermissionType } from '../../../types/permissions';

export type PermissionFormValues = {
  name: string;
  description: string;
  module: string;
  type: PermissionType | '';
  isActive: boolean;
};

type Props = {
  isOpen: boolean;
  title: string;
  moduleOptions: string[];
  typeOptions: string[];
  initialValues: PermissionFormValues;
  isSubmitting: boolean;
  errorMessage?: string | null;
  onClose: () => void;
  onSubmit: (values: PermissionFormValues) => Promise<void> | void;
};

const normalizeValues = (values: PermissionFormValues): PermissionFormValues => ({
  ...values,
  name: values.name.trim(),
  description: values.description.trim(),
  module: values.module.trim()
});

export const PermissionFormModal: React.FC<Props> = ({
  isOpen,
  title,
  moduleOptions,
  typeOptions,
  initialValues,
  isSubmitting,
  errorMessage,
  onClose,
  onSubmit
}) => {
  const [form, setForm] = useState<PermissionFormValues>(initialValues);
  const [touched, setTouched] = useState<{ [K in keyof PermissionFormValues]?: boolean }>({});

  useEffect(() => {
    if (!isOpen) return;
    setForm(initialValues);
    setTouched({});
  }, [isOpen, initialValues]);

  const errors = useMemo(() => {
    const values = normalizeValues(form);
    const next: Partial<Record<keyof PermissionFormValues, string>> = {};

    if (!values.name) next.name = 'Nome é obrigatório.';
    if (values.name.length > 100) next.name = 'Nome deve ter no máximo 100 caracteres.';

    if (!values.description) next.description = 'Descrição é obrigatória.';
    if (values.description.length > 255) next.description = 'Descrição deve ter no máximo 255 caracteres.';

    if (!values.module) next.module = 'Módulo é obrigatório.';
    if (values.module.length > 200) next.module = 'Módulo deve ter no máximo 200 caracteres.';

    if (!values.type) next.type = 'Tipo é obrigatório.';

    return next;
  }, [form]);

  const canSubmit = Object.keys(errors).length === 0 && !isSubmitting;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: name === 'type' ? (value as PermissionType) : value
    }));
  };

  const handleBlur = (field: keyof PermissionFormValues) => {
    setTouched(prev => ({ ...prev, [field]: true }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const values = normalizeValues(form);
    setTouched({ name: true, description: true, module: true, type: true, isActive: true });
    if (Object.keys(errors).length > 0) return;
    await onSubmit(values);
  };

  return (
    <Modal isOpen={isOpen} title={title} onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        {errorMessage ? (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {errorMessage}
          </div>
        ) : null}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
          <div className="relative">
            <input
              name="name"
              type="text"
              className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 ${
                touched.name && errors.name ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'
              }`}
              value={form.name}
              onChange={handleChange}
              onBlur={() => handleBlur('name')}
              maxLength={100}
              disabled={isSubmitting}
              required
            />
            <div className="absolute -bottom-6 right-0 text-xs text-gray-500">
              {form.name.length}/100
            </div>
          </div>
          {touched.name && errors.name ? <p className="text-xs text-red-600 mt-1">{errors.name}</p> : null}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
          <textarea
            name="description"
            className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 ${
              touched.description && errors.description ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'
            }`}
            rows={3}
            value={form.description}
            onChange={handleChange}
            onBlur={() => handleBlur('description')}
            maxLength={255}
            disabled={isSubmitting}
            required
          />
          <div className="text-xs text-gray-500 text-right">{form.description.length}/255</div>
          {touched.description && errors.description ? <p className="text-xs text-red-600 mt-1">{errors.description}</p> : null}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Módulo</label>
            <select
              name="module"
              className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 ${
                touched.module && errors.module ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'
              }`}
              value={form.module}
              onChange={handleChange}
              onBlur={() => handleBlur('module')}
              disabled={isSubmitting}
              required
            >
              <option value="">Selecione um módulo</option>
              {moduleOptions.filter(opt => opt !== 'Todos').map(opt => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
            {touched.module && errors.module ? <p className="text-xs text-red-600 mt-1">{errors.module}</p> : null}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Ação permitida</label>
            <select
              name="type"
              className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 ${
                touched.type && errors.type ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'
              }`}
              value={form.type}
              onChange={handleChange}
              onBlur={() => handleBlur('type')}
              disabled={isSubmitting}
              required
            >
              <option value="">Selecione uma ação</option>
              {typeOptions.filter(opt => opt !== 'Todos').map(opt => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
            {touched.type && errors.type ? <p className="text-xs text-red-600 mt-1">{errors.type}</p> : null}
          </div>
        </div>

        <div className="flex items-center">
          <input
            id="isActive"
            name="isActive"
            type="checkbox"
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            checked={form.isActive}
            onChange={e => setForm(prev => ({ ...prev, isActive: e.target.checked }))}
            onBlur={() => handleBlur('isActive')}
            disabled={isSubmitting}
          />
          <label htmlFor="isActive" className="ml-2 block text-sm text-gray-700">
            Ativa
          </label>
        </div>

        <div className="flex justify-end">
          <Button variant="secondary" type="button" className="mr-2" onClick={onClose} disabled={isSubmitting}>
            Cancelar
          </Button>
          <Button type="submit" disabled={!canSubmit}>
            Salvar
          </Button>
        </div>
      </form>
    </Modal>
  );
};
